import { HEADERS_CONTENT_TYPE, HTTP_METHODS, URLS } from 'modules/api/constants';
import handleRequest from 'modules/api/common';
import { getCampaignViolations } from 'utils/getCampaignViolations';
import { CancelFunctions } from 'components/common/types';
import {
  DirectSalesAssetResponse,
  DirectSalesAvailabilityResponse,
  DirectSalesCampaignDealLineStateResponse,
  DirectSalesCampaignPlanSharableResponse,
  DirectSalesCampaignResponse,
  DirectSalesStatusChangeResponse,
} from 'components/common/types/DirectSalesCampaignResponse.types';
import {
  DirectSalesCampaignDealLineResponse,
  DirectSalesDuplicateLineRequest,
  DirectSalesSplitLineRequest,
  DirectSalesState,
  DirectSalesTransformedDealLineRequest,
  DirectSalesUpdateNegotiationTypesRequest,
  DirectSalesUpdateTransientDealStateRequest,
} from 'components/common/types/DirectSalesCampaign.types';
import { DirectSalesCampaignRequest } from 'components/common/types/DirectSalesCampaignRequest.types';
import { SolutionSummary } from 'components/common/types/SolutionSummary.types';
import createRequest, { getRequestOptions } from './request';

export const getDirectSalesCampaign = (
  id: string,
  marketId: string,
  cancelFunctions: CancelFunctions,
  cancelKey = 'DIRECT_SALES_CAMPAIGN',
  showLoader = true,
): Promise<DirectSalesCampaignResponse> => {
  const reqOptions = {
    url: URLS().DIRECT_SALES_CAMPAIGN(marketId, id),
    method: HTTP_METHODS.GET,
    showLoader,
  };

  return handleRequest(reqOptions, cancelFunctions, cancelKey);
};

export const getOriginalDirectSalesCampaign = (
  id: string,
  marketId: string,
  cancelFunctions: CancelFunctions,
): Promise<DirectSalesCampaignResponse> => {
  const reqOptions = {
    url: URLS().DIRECT_SALES_CAMPAIGN_ORIGINAL(marketId, id),
    method: HTTP_METHODS.GET,
    showLoader: true,
  };

  return handleRequest(reqOptions, cancelFunctions, 'DIRECT_SALES_CAMPAIGN_ORIGINAL');
};

export const editDirectSalesCampaign = (
  data: DirectSalesCampaignRequest,
  marketId: string,
  campaignId: string,
  cancelFunctions: CancelFunctions,
): Promise<DirectSalesCampaignResponse> => {
  const reqOptions = {
    url: URLS().DIRECT_SALES_CAMPAIGN(marketId, campaignId),
    method: HTTP_METHODS.PATCH,
    data,
    headers: {
      'Content-Type': HEADERS_CONTENT_TYPE.APPLICATION_JSON,
    },
    notificationTimeout: 10000,
    skipNotificationFor: (error: unknown) => getCampaignViolations(error).length > 0,
  };

  return handleRequest(reqOptions, cancelFunctions, 'DIRECT_SALES_CAMPAIGN');
};

export const getDirectSalesSolution = (
  id: string,
  marketId: string,
  cancelFunctions: CancelFunctions,
): Promise<DirectSalesCampaignResponse> => {
  const reqOptions = {
    url: URLS().FETCH_DIRECT_SALES_SOLUTION(marketId, id),
    method: HTTP_METHODS.GET,
    notificationTimeout: 10000,
  };

  return handleRequest(reqOptions, cancelFunctions, 'FETCH_DIRECT_SALES_ALLOCATION');
};

export const createDirectSalesCampaign = (
  data: DirectSalesCampaignRequest,
  marketId: string,
  cancelFunctions: CancelFunctions,
): Promise<DirectSalesCampaignResponse> => {
  const reqOptions = {
    url: URLS().CREATE_DIRECT_SALES_CAMPAIGN(marketId),
    method: HTTP_METHODS.POST,
    data,
    headers: {
      'Content-Type': HEADERS_CONTENT_TYPE.APPLICATION_JSON,
    },
    notificationTimeout: 10000,
  };

  return handleRequest(reqOptions, cancelFunctions, 'CREATE_DIRECT_SALES_CAMPAIGN');
};

export const createDirectSalesLine = (
  data: DirectSalesTransformedDealLineRequest,
  marketId: string,
  campaignId: string,
  cancelFunctions: CancelFunctions,
): Promise<{ id: string; lines: DirectSalesCampaignDealLineResponse[] }> => {
  const reqOptions = {
    url: URLS().CREATE_DIRECT_SALES_LINE(marketId, campaignId),
    method: HTTP_METHODS.POST,
    data,
    headers: {
      'Content-Type': HEADERS_CONTENT_TYPE.APPLICATION_JSON,
    },
    notificationTimeout: 10000,
  };

  return handleRequest(reqOptions, cancelFunctions, 'CREATE_DIRECT_SALES_LINE');
};

export const applyProductTemplateToDealLine = (
  marketId: string,
  campaignId: string,
  dealLineId: string,
  templateId: string,
  cancelFunctions: CancelFunctions,
): Promise<void> => {
  const reqOptions = {
    url: URLS().APPLY_PRODUCT_TEMPLATE_TO_DEAL_LINE(marketId, campaignId, dealLineId),
    method: HTTP_METHODS.POST,
    data: { productId: templateId },
    headers: {
      'Content-Type': HEADERS_CONTENT_TYPE.APPLICATION_JSON,
    },
    notificationTimeout: 10000,
  };

  return handleRequest(reqOptions, cancelFunctions, 'APPLY_PRODUCT_TEMPLATE_TO_DEAL_LINE');
};

export const clearProductFromDealLine = (
  marketId: string,
  campaignId: string,
  dealLineId: string,
  cancelFunctions: CancelFunctions,
): Promise<void> => {
  const reqOptions = {
    url: URLS().CLEAR_PRODUCT_FROM_DEAL_LINE(marketId, campaignId, dealLineId),
    method: HTTP_METHODS.PUT,
    headers: {
      'Content-Type': HEADERS_CONTENT_TYPE.APPLICATION_JSON,
    },
    notificationTimeout: 10000,
  };

  return handleRequest(reqOptions, cancelFunctions, 'CLEAR_PRODUCT_FROM_DEAL_LINE');
};

export const checkDirectSalesAvailability = (
  marketId: string,
  campaignId: string,
  dealLineId: string,
  cancelFunctions: CancelFunctions,
): Promise<DirectSalesAvailabilityResponse> => {
  const reqOptions = {
    url: URLS().CHECK_DIRECT_SALES_AVAILABILITY(marketId, campaignId, dealLineId),
    method: HTTP_METHODS.GET,
    notificationTimeout: 10000,
  };

  return handleRequest(reqOptions, cancelFunctions, 'CHECK_DIRECT_SALES_AVAILABILITY');
};

export const fetchCampaignState = (
  marketId: string,
  campaignId: string,
  cancelFunctions: CancelFunctions,
  cancelKey = 'FETCH_DIRECT_SALES_CAMPAIGN_STATE',
): Promise<DirectSalesCampaignDealLineStateResponse> => {
  const reqOptions = {
    url: URLS().FETCH_DIRECT_SALES_CAMPAIGN_STATE(marketId, campaignId),
    method: HTTP_METHODS.GET,
    notificationTimeout: 10000,
  };

  return handleRequest(reqOptions, cancelFunctions, cancelKey);
};

export const fetchDealLineState = (
  marketId: string,
  campaignId: string,
  dealLineId: string,
  cancelFunctions: CancelFunctions,
): Promise<DirectSalesCampaignDealLineStateResponse> => {
  const reqOptions = {
    url: URLS().FETCH_DIRECT_SALES_DEAL_LINE_STATE(marketId, campaignId, dealLineId),
    method: HTTP_METHODS.GET,
    notificationTimeout: 10000,
  };

  return handleRequest(reqOptions, cancelFunctions, 'FETCH_DIRECT_SALES_DEAL_LINE_STATE');
};

export const pollDealLineState = (
  marketId: string,
  campaignId: string,
  dealLineId: string,
  cancelFunctions: CancelFunctions,
): ((callback: unknown, interval?: number) => boolean) => {
  const reqOptions = {
    url: URLS().FETCH_DIRECT_SALES_DEAL_LINE_STATE(marketId, campaignId, dealLineId),
    method: HTTP_METHODS.GET,
    notificationTimeout: 10000,
  };

  const { poll, cancel } = createRequest(reqOptions);

  if (cancelFunctions.FETCH_DIRECT_SALES_DEAL_LINE_STATE) cancelFunctions.FETCH_DIRECT_SALES_DEAL_LINE_STATE();
  cancelFunctions.FETCH_DIRECT_SALES_DEAL_LINE_STATE = cancel;

  return poll;
};

export const pollCampaignState = (
  marketId: string,
  campaignId: string,
  cancelFunctions: CancelFunctions,
): ((callback: unknown, interval?: number) => boolean) => {
  const reqOptions = {
    url: URLS().FETCH_DIRECT_SALES_CAMPAIGN_STATE(marketId, campaignId),
    method: HTTP_METHODS.GET,
    notificationTimeout: 10000,
  };

  const { poll, cancel } = createRequest(reqOptions);

  if (cancelFunctions.FETCH_DIRECT_SALES_CAMPAIGN_STATE) cancelFunctions.FETCH_DIRECT_SALES_CAMPAIGN_STATE();
  cancelFunctions.FETCH_DIRECT_SALES_CAMPAIGN_STATE = cancel;

  return poll;
};

export const updateDirectSalesDealLine = (
  data: DirectSalesTransformedDealLineRequest,
  marketId: string,
  campaignId: string,
  dealLineId: string,
  cancelFunctions: CancelFunctions,
): Promise<{
  id: string;
  dealLines: [{ id: string; name: string }];
}> => {
  const reqOptions = {
    url: URLS().UPDATE_DIRECT_SALES_DEAL_LINE(marketId, campaignId, dealLineId),
    method: HTTP_METHODS.PUT,
    data,
    headers: {
      'Content-Type': HEADERS_CONTENT_TYPE.APPLICATION_JSON,
    },
    notificationTimeout: 10000,
  };

  return handleRequest(reqOptions, cancelFunctions, 'UPDATE_DIRECT_SALES_DEAL_LINE');
};

export const duplicateDirectSalesDealLine = (
  data: DirectSalesDuplicateLineRequest,
  marketId: string,
  campaignId: string,
  dealLineId: string,
  cancelFunctions: CancelFunctions,
): Promise<{
  dealLineId: string;
}> => {
  const reqOptions = {
    url: URLS().DUPLICATE_DIRECT_SALES_DEAL_LINE(marketId, campaignId, dealLineId),
    method: HTTP_METHODS.POST,
    data,
    headers: {
      'Content-Type': HEADERS_CONTENT_TYPE.APPLICATION_JSON,
    },
    notificationTimeout: 10000,
  };

  return handleRequest(reqOptions, cancelFunctions, 'DUPLICATE_DIRECT_SALES_DEAL_LINE');
};

export const deleteDirectSalesDealLine = (
  marketId: string,
  campaignId: string,
  lineId: string,
  cancelFunctions: CancelFunctions,
): Promise<{
  id: string;
  dealLines: [{ id: string; name: string }];
}> => {
  const reqOptions = {
    url: URLS().DELETE_DIRECT_SALES_DEAL_LINE(marketId, campaignId, lineId),
    method: HTTP_METHODS.DELETE,
    headers: {
      'Content-Type': HEADERS_CONTENT_TYPE.APPLICATION_JSON,
    },
    notificationTimeout: 10000,
  };

  return handleRequest(reqOptions, cancelFunctions, 'DELETE_DIRECT_SALES_DEAL_LINE');
};

export const splitDirectSalesDealLine = (
  data: DirectSalesSplitLineRequest,
  marketId: string,
  campaignId: string,
  dealLineId: string,
  cancelFunctions: CancelFunctions,
): Promise<{
  splitDealLineId: string;
}> => {
  const reqOptions = {
    url: URLS().SPLIT_DIRECT_SALES_DEAL_LINE(marketId, campaignId, dealLineId),
    method: HTTP_METHODS.POST,
    data,
    headers: {
      'Content-Type': HEADERS_CONTENT_TYPE.APPLICATION_JSON,
    },
    notificationTimeout: 10000,
  };

  return handleRequest(reqOptions, cancelFunctions, 'UPDATE_DIRECT_SALES_DEAL_LINE');
};

export const terminateDirectSalesDealLine = (
  data: { splitPoint: string },
  marketId: string,
  campaignId: string,
  dealLineId: string,
  cancelFunctions: CancelFunctions,
): Promise<void> => {
  const reqOptions = {
    url: URLS().TERMINATE_DIRECT_SALES_DEAL_LINE(marketId, campaignId, dealLineId),
    method: HTTP_METHODS.POST,
    data,
    headers: {
      'Content-Type': HEADERS_CONTENT_TYPE.APPLICATION_JSON,
    },
    notificationTimeout: 10000,
  };

  return handleRequest(reqOptions, cancelFunctions, 'TERMINATE_DIRECT_SALES_DEAL_LINE');
};

export const updateDealLineState = (
  data: string,
  marketId: string,
  campaignId: string,
  dealLineId: string,
  cancelFunctions: CancelFunctions,
): Promise<DirectSalesStatusChangeResponse> => {
  const reqOptions = {
    url: URLS().UPDATE_DIRECT_SALES_DEAL_LINE_STATE(marketId, campaignId, dealLineId),
    method: HTTP_METHODS.PUT,
    data,
    showLoader: false,
    headers: {
      'Content-Type': HEADERS_CONTENT_TYPE.APPLICATION_JSON,
    },
    notificationTimeout: 10000,
  };

  return handleRequest(reqOptions, cancelFunctions, 'UPDATE_DIRECT_SALES_DEAL_LINE_STATE');
};

export const updateTransientDealLineState = (
  data: string,
  marketId: string,
  campaignId: string,
  cancelFunctions: CancelFunctions,
): Promise<DirectSalesStatusChangeResponse> => {
  const reqOptions = {
    url: URLS().UPDATE_DIRECT_SALES_TRANSIENT_DEAL_LINE_STATE(marketId, campaignId),
    method: HTTP_METHODS.PUT,
    data,
    showLoader: false,
    headers: {
      'Content-Type': HEADERS_CONTENT_TYPE.APPLICATION_JSON,
    },
    notificationTimeout: 10000,
  };

  return handleRequest(reqOptions, cancelFunctions, 'UPDATE_DIRECT_SALES_TRANSIENT_DEAL_LINE_STATE');
};

export const updateCampaignState = (
  data: string,
  marketId: string,
  campaignId: string,
  cancelFunctions: CancelFunctions,
): Promise<void> => {
  const reqOptions = {
    url: URLS().UPDATE_DIRECT_SALES_CAMPAIGN_STATE(marketId, campaignId),
    method: HTTP_METHODS.PUT,
    data,
    showLoader: false,
    headers: {
      'Content-Type': HEADERS_CONTENT_TYPE.APPLICATION_JSON,
    },
    notificationTimeout: 10000,
  };

  return handleRequest(reqOptions, cancelFunctions, 'UPDATE_DIRECT_SALES_CAMPAIGN_STATE');
};

export const acceptOrDiscardChanges = (
  data: DirectSalesUpdateTransientDealStateRequest,
  marketId: string,
  campaignId: string,
  cancelFunctions: CancelFunctions,
): Promise<void> => {
  const reqOptions = {
    url: URLS().UPDATE_TRANSIENT_DEAL_STATE(marketId, campaignId),
    method: HTTP_METHODS.PUT,
    data,
    headers: {
      'Content-Type': HEADERS_CONTENT_TYPE.APPLICATION_JSON,
    },
    notificationTimeout: 10000,
  };

  return handleRequest(reqOptions, cancelFunctions, 'UPDATE_TRANSIENT_DEAL_STATE');
};

export const terminateCampaignSession = async (publisherId: string, campaignId: string): Promise<unknown> => {
  return fetch(URLS().TERMINATE_DIRECT_SALES_CAMPAIGN_SESSION(publisherId, campaignId), {
    keepalive: true,
    method: 'POST',
    headers: { ...getRequestOptions().headers, 'Content-Type': HEADERS_CONTENT_TYPE.APPLICATION_JSON },
  });
};

export const extendCampaignSessionLock = (
  publisherId: string,
  campaignId: string,
  cancelFunctions: CancelFunctions,
): Promise<void> => {
  const reqOptions = {
    url: URLS().EXTEND_DIRECT_SALES_CAMPAIGN_SESSION_LOCK(publisherId, campaignId),
    method: HTTP_METHODS.PUT,
    headers: {
      'Content-Type': HEADERS_CONTENT_TYPE.APPLICATION_JSON,
    },
  };

  return handleRequest(reqOptions, cancelFunctions, 'EXTEND_DIRECT_SALES_CAMPAIGN_SESSION_LOCK');
};

export const pollDealSalesCampaign = (
  marketId: string,
  campaignId: string,
  cancelFunctions: CancelFunctions,
): ((callback: unknown, interval?: number) => void) => {
  const reqOptions = {
    url: URLS().DIRECT_SALES_CAMPAIGN(marketId, campaignId),
    method: HTTP_METHODS.GET,
    showLoader: true,
    notificationTimeout: 10000,
  };

  const { poll, cancel } = createRequest(reqOptions);

  if (cancelFunctions.DIRECT_SALES_CAMPAIGN) cancelFunctions.DIRECT_SALES_CAMPAIGN();
  cancelFunctions.DIRECT_SALES_CAMPAIGN = cancel;

  return poll;
};

export const getSolutionSummary = (
  campaignId: string,
  marketId: string,
  cancelFunctions: CancelFunctions,
): Promise<SolutionSummary> => {
  const reqOptions = {
    url: URLS().FETCH_DIRECT_SALES_SOLUTION_SUMMARY(marketId, campaignId),
    method: HTTP_METHODS.GET,
    showLoader: true,
  };

  return handleRequest(reqOptions, cancelFunctions, 'FETCH_DIRECT_SALES_SOLUTION_SUMMARY');
};

export const enableDirectSalesSharedCampaign = (
  campaignId: string,
  marketId: string,
  cancelFunctions: CancelFunctions,
): Promise<DirectSalesCampaignResponse> => {
  const reqOptions = {
    url: URLS().ENABLE_DIRECT_SALES_SHARED_CAMPAIGN(marketId, campaignId),
    method: HTTP_METHODS.PUT,
    showLoader: true,
  };

  return handleRequest(reqOptions, cancelFunctions, 'ENABLE_DIRECT_SALES_SHARED_CAMPAIGN');
};

export const disableDirectSalesSharedCampaign = (
  campaignId: string,
  marketId: string,
  cancelFunctions: CancelFunctions,
): Promise<DirectSalesCampaignResponse> => {
  const reqOptions = {
    url: URLS().DISABLE_DIRECT_SALES_SHARED_CAMPAIGN(marketId, campaignId),
    method: HTTP_METHODS.DELETE,
    showLoader: true,
  };

  return handleRequest(reqOptions, cancelFunctions, 'DISABLE_DIRECT_SALES_SHARED_CAMPAIGN');
};

export const getIsDirectSalesCampaignSharable = (
  campaignId: string,
  marketId: string,
  cancelFunctions: CancelFunctions,
): Promise<DirectSalesCampaignPlanSharableResponse> => {
  const reqOptions = {
    url: URLS().FETCH_CAMPAIGN_IS_SHARABLE(marketId, campaignId),
    method: HTTP_METHODS.GET,
    ignoreNotifyError: true,
  };

  return handleRequest(reqOptions, cancelFunctions, 'FETCH_CAMPAIGN_IS_SHARABLE');
};

export const updateDirectSalesCampaignGrid = (
  data: DirectSalesUpdateNegotiationTypesRequest,
  campaignId: string,
  marketId: string,
  cancelFunctions: CancelFunctions,
): Promise<{ updatedKeys: string[]; unchangedKeys: string[] }> => {
  const reqOptions = {
    url: URLS().UPDATE_DIRECT_SALES_GRID(marketId, campaignId),
    method: HTTP_METHODS.PATCH,
    data,
  };

  return handleRequest(reqOptions, cancelFunctions, 'UPDATE_DIRECT_SALES_GRID');
};

export const fetchCampaignFrameListReport = (
  marketId: string,
  campaignId: string,
  cancelFunctions: CancelFunctions,
  dealLineIds?: string[],
): Promise<{ report: {}; metadata: {} }> => {
  const reqOptions = {
    url: URLS().FETCH_DIRECT_SALES_CAMPAIGN_FRAME_LIST_REPORT(marketId, campaignId),
    method: HTTP_METHODS.POST,
    data: {
      dealLineIds,
    },
    showLoader: true,
  };

  return handleRequest(reqOptions, cancelFunctions, 'FETCH_DIRECT_SALES_CAMPAIGN_FRAME_LIST_REPORT');
};

export const fetchCampaignLinesInfo = (
  marketId: string,
  campaignId: string,
  cancelFunctions: CancelFunctions,
): Promise<{ id: string; dealLines: { id: string; name: string; state: DirectSalesState }[] }> => {
  const reqOptions = {
    url: URLS().FETCH_DIRECT_SALES_CAMPAIGN_LINES_INFO(marketId, campaignId),
    method: HTTP_METHODS.GET,
  };

  return handleRequest(reqOptions, cancelFunctions, 'FETCH_DIRECT_SALES_CAMPAIGN_LINES_INFO');
};

export const getDirectSalesAssets = (
  marketId: string,
  campaignId: string,
  dealLineId: string,
  query: string,
  cancelFunctions: CancelFunctions,
): Promise<DirectSalesAssetResponse> => {
  const reqOptions = {
    url: URLS().SEARCH_DIRECT_SALES_ASSETS(marketId, campaignId, dealLineId, query),
    method: HTTP_METHODS.GET,
  };

  return handleRequest(reqOptions, cancelFunctions, 'SEARCH_DIRECT_SALES_ASSETS');
};
